import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { Calendar, Clock, User, Users, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import SignInOverlay from "@/components/SignInOverlay";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Seminars = () => {
  const [seminars, setSeminars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeminar, setSelectedSeminar] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    smsConsent: false,
  });

  useEffect(() => {
    const fetchSeminars = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/seminars`);
        setSeminars(response.data);
      } catch (error) {
        console.error("Error fetching seminars:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSeminars();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all fields");
      return;
    }

    setRegistering(true);
    try {
      await axios.post(`${API_URL}/api/seminars/register`, {
        seminar_id: selectedSeminar.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      toast.success("You're registered! Check your email for details.");
      setSelectedSeminar(null);
      setFormData({ name: "", email: "", phone: "", smsConsent: false });
      
      // Refresh seminars to update spots
      const response = await axios.get(`${API_URL}/api/seminars`);
      setSeminars(response.data);
    } catch (error) {
      if (error.response?.data?.detail === "No spots available") {
        toast.error("Sorry, this seminar is full!");
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } finally {
      setRegistering(false);
    }
  };

  return (
    <SignInOverlay message="Sign in to access seminars and events">
    <div className="min-h-screen bg-slate-50" data-testid="seminars-page">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Home */}
          <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6 text-sm font-medium">
            ← Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              Free Events
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-navy-700 mb-4">
              Live Seminars & Workshops
            </h1>
            <p className="text-lg text-slate-600">
              Join our free online sessions and learn investing with other students.
              Ask questions, get answers, and build confidence together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative -mt-8 z-10">
        <div className="max-w-5xl mx-auto px-4">
          <img
            src="https://images.unsplash.com/photo-1765438869297-6fa4b627906a?w=1200&q=80"
            alt="Financial seminar"
            className="w-full h-[300px] object-cover rounded-2xl shadow-floating"
          />
        </div>
      </section>

      {/* Seminars List */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-heading font-bold text-navy-700 mb-8">
            Upcoming Sessions
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-slate-200 rounded w-1/2 mb-4" />
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : seminars.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <p className="text-slate-500">No upcoming seminars at the moment.</p>
                <p className="text-sm text-slate-400 mt-2">
                  Join our newsletter to be notified of new sessions!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {seminars.map((seminar, index) => {
                const spotsLeft = seminar.spots_available - seminar.spots_taken;
                const isFull = spotsLeft <= 0;

                return (
                  <motion.div
                    key={seminar.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="border-0 shadow-card overflow-hidden bg-white">
                      <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row">
                          {/* Date Column */}
                          <div className="bg-emerald-500 text-white p-6 flex flex-col items-center justify-center min-w-[120px]">
                            <Calendar className="w-6 h-6 mb-2" />
                            <p className="text-2xl font-bold">
                              {new Date(seminar.date).getDate()}
                            </p>
                            <p className="text-sm opacity-80">
                              {new Date(seminar.date).toLocaleDateString("en-US", {
                                month: "short",
                              })}
                            </p>
                          </div>

                          {/* Content */}
                          <div className="flex-1 p-6">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                              <div>
                                <h3 className="font-heading font-bold text-xl text-navy-700 mb-2">
                                  {seminar.title}
                                </h3>
                                <p className="text-slate-600 text-sm mb-3">
                                  {seminar.description}
                                </p>
                                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {seminar.time}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <User className="w-4 h-4" />
                                    {seminar.speaker}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    {isFull ? (
                                      <span className="text-red-500">Full</span>
                                    ) : (
                                      `${spotsLeft} spots left`
                                    )}
                                  </span>
                                </div>
                              </div>

                              <Button
                                onClick={() => setSelectedSeminar(seminar)}
                                disabled={isFull}
                                className={
                                  isFull
                                    ? "bg-slate-200 text-slate-400"
                                    : "bg-emerald-500 hover:bg-emerald-600 text-white"
                                }
                                data-testid={`register-${seminar.id}`}
                              >
                                {isFull ? "Sold Out" : "Reserve My Spot"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Registration Modal */}
      <Dialog
        open={!!selectedSeminar}
        onOpenChange={() => setSelectedSeminar(null)}
      >
        <DialogContent className="sm:max-w-md" data-testid="seminar-register-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Reserve Your Spot</DialogTitle>
            <DialogDescription>
              {selectedSeminar?.title} • {selectedSeminar?.date} at {selectedSeminar?.time}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRegister} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Your name"
                data-testid="seminar-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="you@university.ca"
                data-testid="seminar-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="+1 (555) 000-0000"
                data-testid="seminar-phone-input"
              />
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="smsConsent"
                checked={formData.smsConsent}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, smsConsent: checked })
                }
              />
              <Label htmlFor="smsConsent" className="text-xs text-slate-500">
                I agree to receive SMS reminders about this seminar. Msg & data rates
                may apply.
              </Label>
            </div>

            <Button
              type="submit"
              disabled={registering}
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              data-testid="seminar-submit-btn"
            >
              {registering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Registration
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      
    </div>
    </SignInOverlay>
  );
};

export default Seminars;
